/**
 * UI-state samples for the Metrics Sprint collectors (Q-336), written to the
 * call-less browser telemetry sink (ui_query_telemetry via measureRead's
 * buffer): a page reaching its first rendered state (A96), and a crash screen
 * shown (A142). Raw samples only; the collectors compute the metrics.
 */
import { recordUiTelemetrySample } from './measureRead';

/** A96's target (training dashboard load ≤ 3 s); the collector judges from elapsed_ms, never from the outcome. */
export const TRAINING_DASHBOARD_BUDGET_MS = 3000;

/**
 * Sentinel: not a budgeted read. The sink requires budget_ms > 0; a crash
 * screen has no budget, and the A142 collector counts rows by shape_key and
 * ignores budget_ms and outcome, so this value never reaches a latency metric.
 */
export const CRASH_SCREEN_BUDGET_SENTINEL_MS = 1;

export type DashboardTerminalState = 'data' | 'empty' | 'error' | 'nochart';

/**
 * The training dashboard's terminal state, by the same rules as the load probe
 * (chief-of-staff state/perf-metrics/queries/E-R21-dashboard-load.py): the
 * "Total Calls Analyzed" stat is shown (skeleton gone), and one of: the error
 * box, the score chart, the onboarding card, or the "complete training calls"
 * placeholder.
 */
export function detectTrainingDashboardState(doc: Document = document): DashboardTerminalState | null {
  // Only the dashboard's own root: an error box elsewhere (the training
  // sidebar's "Start Training" failure) is not the dashboard's state.
  const root = doc.querySelector('[data-training-dashboard]');
  if (!root) return null;
  const text = (root.textContent ?? '').toLowerCase();
  if (!text.includes('total calls analyzed')) return null;
  if (root.querySelector('div.h-28.animate-pulse')) return null;
  if (root.querySelector('div.bg-red-500\\/10')) return 'error';
  if (root.querySelector('.recharts-line')) return 'data';
  if (text.includes('welcome to training')) return 'empty';
  if (text.includes('complete training calls to see your score progression')) return 'nochart';
  return null;
}

/**
 * How the page was reached. A hard load (this URL is the document's own) is
 * timed from navigation start and is `cold` when the document came over the
 * network; an in-app (soft) navigation is timed from the page's mount, since
 * performance.now() then includes time spent on earlier pages.
 */
// Only the first dashboard mount of a document can be its hard load: coming
// back to the page later without a reload is an in-app navigation.
let hardLoadConsumed = false;

export function navigationOf(pathname: string, perf: Performance = performance): {
  flags: Record<string, boolean>;
  soft: boolean;
} {
  const entry = perf.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
  let documentPath: string | null = null;
  try {
    documentPath = entry ? new URL(entry.name).pathname : null;
  } catch {
    documentPath = null;
  }
  if (!entry || documentPath !== pathname || hardLoadConsumed) return { flags: { nav_soft: true }, soft: true };
  hardLoadConsumed = true;
  const type = entry.type === 'back_forward' ? 'back_forward' : entry.type === 'reload' ? 'reload' : 'navigate';
  const cold = type === 'navigate' && entry.transferSize > 0;
  return { flags: { [`nav_${type}`]: true, [cold ? 'cold' : 'warm']: true }, soft: false };
}

/**
 * Report the first frame the dashboard shows a terminal state. The DOM is
 * checked now and on every change to it (no polling while the page waits on
 * its data, however long that takes — the slowest loads are the metric's
 * tail); the sample is taken at the paint after the state appeared. Returns a
 * cancel function (unmount before the page settled: no sample).
 */
export function startTrainingDashboardRenderTelemetry(): () => void {
  if (typeof window === 'undefined' || typeof MutationObserver !== 'function') return () => {};
  const mountedAt = performance.now();
  const navigation = navigationOf(window.location.pathname);
  let done = false;
  let pending: { state: DashboardTerminalState; cancel: () => void } | null = null;
  const observer = new MutationObserver(() => check());
  const finish = () => {
    done = true;
    observer.disconnect();
    pending?.cancel();
  };
  function check() {
    if (done) return;
    const state = detectTrainingDashboardState();
    if (pending && pending.state === state) return;
    // The state changed (or went away) before it was painted: start over.
    pending?.cancel();
    pending = null;
    if (state === null) return;
    // The first frame showing the state is the stop; the next frame confirms
    // it was still on screen (a state replaced before its paint never counts).
    let firstFrameAt: number | null = null;
    const cancel = frames(
      () => { firstFrameAt = performance.now(); },
      () => {
        if (done || detectTrainingDashboardState() !== state || firstFrameAt === null) return;
        finish();
        recordUiTelemetrySample({
          shapeKey: `page_rendered.training_dashboard.${state}`,
          elapsedMs: Math.round(navigation.soft ? firstFrameAt - mountedAt : firstFrameAt),
          budgetMs: TRAINING_DASHBOARD_BUDGET_MS,
          failed: state === 'error',
          flags: navigation.flags,
          surface: '/training/dashboard',
        });
      },
    );
    pending = { state, cancel };
  }
  observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
  check();
  return finish;
}

/** Run `first` in the next animation frame and `second` in the one after. */
function frames(first: () => void, second: () => void): () => void {
  if (typeof requestAnimationFrame !== 'function') {
    first();
    second();
    return () => {};
  }
  let next: number | null = null;
  const id = requestAnimationFrame(() => {
    first();
    next = requestAnimationFrame(second);
  });
  return () => {
    cancelAnimationFrame(id);
    if (next !== null) cancelAnimationFrame(next);
  };
}

const crashesReported = new Set<string>();

/** Error classes named as a crash reason; anything else (a custom or mutated name could carry data) is unknown_error. */
const KNOWN_ERROR_CLASSES: ReadonlySet<string> = new Set([
  'Error', 'TypeError', 'ReferenceError', 'RangeError', 'SyntaxError', 'URIError', 'EvalError',
  'AggregateError', 'ChunkLoadError', 'AbortError', 'NetworkError', 'SecurityError', 'NotFoundError',
  'InvalidStateError', 'QuotaExceededError', 'TimeoutError',
]);

/** The route's first segment only ("/calls", "/training"): never an id from the rest of the path. */
function staticRoute(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0];
  return first && /^[a-z][a-z0-9-]{0,40}$/.test(first) ? `/${first}` : '/';
}

/**
 * A crash screen was shown (an error boundary rendered its fallback). One
 * sample per boundary per page load; the error's class name only — never its
 * message, which can carry user data.
 */
export function reportCrashScreenShown(boundary: string, error: unknown): void {
  if (typeof window === 'undefined' || crashesReported.has(boundary)) return;
  crashesReported.add(boundary);
  const name = error instanceof Error && KNOWN_ERROR_CLASSES.has(error.name) ? error.name : 'unknown_error';
  recordUiTelemetrySample({
    shapeKey: `crash_screen_shown.${boundary}`,
    // A142 counts rows; elapsed is not used (and a long-lived tab's
    // performance.now() can exceed the sink's one-day bound).
    elapsedMs: 0,
    budgetMs: CRASH_SCREEN_BUDGET_SENTINEL_MS,
    failed: true,
    flags: { [`reason_${name}`]: true },
    surface: staticRoute(window.location.pathname),
  });
}

/** Test seam. */
export function resetCrashScreenReportsForTest(): void {
  crashesReported.clear();
  hardLoadConsumed = false;
}
